import {
  Injectable,
  EmbeddedViewRef,
  ComponentRef,
  Injector,
} from '@angular/core';
import {
  WindowConfig,
  DEFAULT_WINDOW_CONFIG,
} from './window-config';
import {
  OverlayConfig,
  OverlayRef,
  Overlay,
} from '@angular/cdk/overlay';
import {
  TemplatePortal,
  ComponentPortal,
} from '@angular/cdk/portal';
import { WindowRef } from './window-ref';
import { WINDOW_CONTEXT } from './tokens';
import { WindowContext } from './window-context';
import { NGXLogger } from 'ngx-logger';
import {
  DX_DOCUMENT_ID_TOKEN,
  DX_DOCUMENT_TOKEN,
} from '../tokens';

@Injectable({
  providedIn: 'root'
})
export class WindowService {

  constructor(
    private injector: Injector,
    private overlay: Overlay,
    private logger: NGXLogger
  ) {}

  public open<T>(config: WindowConfig<any, T> = {}) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_WINDOW_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const windowRef = new WindowRef(overlayRef, this.overlay);

    const overlayComponent = this.attachDialogContainer(
      overlayRef,
      dialogConfig,
      windowRef
    );

    overlayRef.backdropClick().subscribe((_) => windowRef.close());

    return windowRef;
  }

  private createOverlay<T>(config: WindowConfig<any, T>) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachDialogContainer<D, T>(
    overlayRef: OverlayRef,
    config: WindowConfig<D, T>,
    windowRef: WindowRef
  ) {
    this.logger.trace('[WindowService::attachDialogContainer] call', { overlayRef, config, windowRef });
    if (config.component) {
      const injector = Injector.create({
        parent:    config.injector || this.injector,
        providers: [
          {
            provide:  WINDOW_CONTEXT,
            useValue: {
                        id:   config.id,
                        overlayRef,
                        windowRef,
                        data: config.data
                      } as WindowContext<D>
          },
          {
            provide:  DX_DOCUMENT_ID_TOKEN,
            useValue: config.data && config.data[ 'id' ] || null,
          },
          {
            provide:  DX_DOCUMENT_TOKEN,
            useValue: config.data || null,
          }
        ]
      });

      const containerPortal               = new ComponentPortal(
        config.component,
        config.viewContainerRef,
        injector,
        config.componentFactoryResolver
      );
      const containerRef: ComponentRef<T> = overlayRef.attach(containerPortal);

      return containerRef.instance;
    } else if (config.template) {
      const containerPortal                  = new TemplatePortal(
        config.template,
        config.viewContainerRef
      );
      const containerRef: EmbeddedViewRef<T> = overlayRef.attach(
        containerPortal
      );

      return containerRef.context;
    }
  }

  private getOverlayConfig<T>(
    config: WindowConfig<any, T>
  ): OverlayConfig {
    const positionStrategy = this.overlay
                                 .position()
                                 .global()
                                 .top('100px')
                                 .left('300px');

    return new OverlayConfig({
      hasBackdrop:    false,
      panelClass:     config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width:          config.width,
      height:         config.height,
      maxWidth:       '100vw',
      maxHeight:      '100vh',
      positionStrategy
    });
  }

}
